/**
 * Discover Contract Schema
 * Zod schemas for parsing and validating filter state
 */
import { z } from 'zod';
import type { DiscoverFilters, SortMode } from './types';
import { DEFAULT_FILTERS } from './types';

/** Valid sort modes */
export const SORT_MODES: SortMode[] = ['relevance', 'latest', 'price_asc', 'price_desc'];

/** Zod schema for sort mode */
export const sortModeSchema = z.enum(['relevance', 'latest', 'price_asc', 'price_desc']);

/** Zod schema for discover filters (supports both v2 and v3) */
export const discoverFiltersSchema = z.object({
  q: z.string().default(''),
  subjects: z.array(z.string()).default([]),
  geo: z.array(z.string()).default([]),
  brand_categories: z.array(z.string()).default([]),
  psychographics: z.array(z.string()).default([]),
  brand_geographic_focus: z.array(z.string()).default([]),
  sort: sortModeSchema.default('relevance'),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(24),
  // V3 taxonomy filters
  industry_ids: z.array(z.string()).default([]),
  category_ids: z.array(z.string()).default([]),
  subcategory_ids: z.array(z.string()).default([]),
  hashtag_ids: z.array(z.string()).default([]),
  // Range filters
  price_min: z.coerce.number().optional(),
  price_max: z.coerce.number().optional(),
  discount_min: z.coerce.number().optional(),
  discount_max: z.coerce.number().optional(),
  commission_min: z.coerce.number().optional(),
  commission_max: z.coerce.number().optional(),
});

/**
 * Parse filters from unknown input (e.g., URL search params)
 * Returns validated filters with defaults for missing values
 */
export function parseFilters(input: unknown): DiscoverFilters {
  try {
    return discoverFiltersSchema.parse(input);
  } catch {
    return { ...DEFAULT_FILTERS };
  }
}

/**
 * Parse filters from URLSearchParams
 * Handles array params (comma-separated or multiple values)
 */
export function parseFiltersFromSearchParams(params: URLSearchParams): DiscoverFilters {
  const parseArray = (key: string): string[] => {
    const values = params.getAll(key);
    if (values.length === 0) {
      const single = params.get(key);
      if (single) {
        return single.split(',').map(s => s.trim()).filter(Boolean);
      }
      return [];
    }
    // Flatten comma-separated values
    return values.flatMap(v => v.split(',').map(s => s.trim()).filter(Boolean));
  };

  const raw = {
    q: params.get('q') || '',
    subjects: parseArray('subjects'),
    geo: parseArray('geo'),
    brand_categories: parseArray('brand_categories'),
    psychographics: parseArray('psychographics'),
    brand_geographic_focus: parseArray('brand_geographic_focus'),
    sort: params.get('sort') || 'relevance',
    page: params.get('page') || '1',
    pageSize: params.get('pageSize') || '24',
    // V3 taxonomy params
    industry_ids: parseArray('industry_ids'),
    category_ids: parseArray('category_ids'),
    subcategory_ids: parseArray('subcategory_ids'),
    hashtag_ids: parseArray('hashtag_ids'),
    // Range filters
    ...(params.get('price_min') ? { price_min: params.get('price_min') } : {}),
    ...(params.get('price_max') ? { price_max: params.get('price_max') } : {}),
    ...(params.get('discount_min') ? { discount_min: params.get('discount_min') } : {}),
    ...(params.get('discount_max') ? { discount_max: params.get('discount_max') } : {}),
    ...(params.get('commission_min') ? { commission_min: params.get('commission_min') } : {}),
    ...(params.get('commission_max') ? { commission_max: params.get('commission_max') } : {}),
  };

  return parseFilters(raw);
}

/**
 * Check if filters have any active selections
 */
export function hasActiveFilters(filters: DiscoverFilters): boolean {
  return (
    filters.q.length > 0 ||
    filters.subjects.length > 0 ||
    filters.geo.length > 0 ||
    filters.brand_categories.length > 0 ||
    filters.psychographics.length > 0 ||
    filters.brand_geographic_focus.length > 0 ||
    (filters.industry_ids?.length ?? 0) > 0 ||
    (filters.category_ids?.length ?? 0) > 0 ||
    (filters.subcategory_ids?.length ?? 0) > 0 ||
    (filters.hashtag_ids?.length ?? 0) > 0 ||
    filters.price_min != null ||
    filters.price_max != null ||
    filters.discount_min != null ||
    filters.discount_max != null ||
    filters.commission_min != null ||
    filters.commission_max != null
  );
}

/**
 * Check if v3 taxonomy filters are active
 */
export function hasV3Filters(filters: DiscoverFilters): boolean {
  return (
    (filters.industry_ids?.length ?? 0) > 0 ||
    (filters.category_ids?.length ?? 0) > 0 ||
    (filters.subcategory_ids?.length ?? 0) > 0
  );
}

/**
 * Get count of active filter selections
 */
export function countActiveFilters(filters: DiscoverFilters): number {
  return (
    (filters.q.length > 0 ? 1 : 0) +
    filters.subjects.length +
    filters.geo.length +
    filters.brand_categories.length +
    filters.psychographics.length +
    filters.brand_geographic_focus.length +
    (filters.industry_ids?.length ?? 0) +
    (filters.category_ids?.length ?? 0) +
    (filters.subcategory_ids?.length ?? 0) +
    (filters.hashtag_ids?.length ?? 0)
  );
}
